import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import {
  createMapMarkerElement,
  createUserMarkerElement,
} from '../components/guest-map/mapStyleUtils';
import { getVenueImage } from '../components/guest-map/venueImage';
import { createExperienceMarkerElement } from '../components/guest-map/experienceMapMarker';
import { spreadOverlappingMarkers } from '../components/guest-map/markerCollision';

// ─────────────────────────────────────────────────────────────────
// Continuous zoom-driven size for map markers.
//
// Why continuous instead of discrete bands?
//   The previous banded implementation destroyed and recreated every
//   venue marker when the zoom crossed a threshold, AND switched the
//   MapLibre `anchor` from 'bottom' (photo pin) to 'center' (dot).
//   Changing the anchor mid-gesture caused the pin to visually "jump"
//   relative to its geographic coordinate — user-reported "pins move
//   like crazy when I zoom in or out". Experience pins (fixed anchor
//   'center', fixed size) never jumped, which is why they were the
//   only steady markers on the map.
//
// New approach:
//   - `anchor` is constant for every marker type across every zoom.
//   - Size is a smooth lerp of `map.getZoom()` clamped to a working
//     range (Attica-wide view → street-level view).
//   - Size is applied via direct DOM mutation on each `zoom` event —
//     NO React re-renders, NO marker rebuilds.
//   - Venue photo is ALWAYS visible; we never swap to a plain dot.
// ─────────────────────────────────────────────────────────────────
const VENUE_SIZE_MAX = 46;          // zoomed in (street level)
const VENUE_SIZE_MIN = 24;          // zoomed out (whole Attica)
const EXPERIENCE_SIZE_MAX = 22;     // current "big" experience pin
const EXPERIENCE_SIZE_MIN = 10;     // whole-Attica experience pin
const ZOOM_FULL = 14;               // ≥ this → max size
const ZOOM_ATTICA = 9;              // ≤ this → min size (whole Attica fits)

function lerp(a, b, t) { return a + (b - a) * t; }

function zoomProgress(zoom) {
  if (zoom >= ZOOM_FULL) return 1;
  if (zoom <= ZOOM_ATTICA) return 0;
  return (zoom - ZOOM_ATTICA) / (ZOOM_FULL - ZOOM_ATTICA);
}

function venueSizeFor(zoom) {
  return Math.round(lerp(VENUE_SIZE_MIN, VENUE_SIZE_MAX, zoomProgress(zoom)));
}

function experienceSizeFor(zoom) {
  return Math.round(lerp(EXPERIENCE_SIZE_MIN, EXPERIENCE_SIZE_MAX, zoomProgress(zoom)));
}

// Combine the zoom-driven size scale with the "selected" visual state.
// CRITICAL: we NEVER touch the OUTER MapLibre marker element's transform
// — only the inner `[data-marker-inner]` wrap — because MapLibre owns
// the outer transform for geographic positioning. We ALSO never touch
// the outer element's width/height, because changing the DOM box
// during a zoom tick perturbs MapLibre's anchor math and the pin
// visually swims relative to the geo coord (user: "pins still moving
// when I zoom"). The outer box stays at the max size and the pin
// visually shrinks/grows via this scale transform on the inner wrap.
function applyVenueWrapTransform(wrap, sizeScale, isSelected) {
  // Pure scale transform on the inner wrap. No `translateZ(0)` — the
  // round-6 fix (per Emergent support guidance) strips every decoration
  // outside the circle, including the compositor-layer hint, so the
  // wrap paints on the same layer MapLibre itself positions. Keeping
  // the transform minimal is what makes the pin anchor stable across
  // zoom frames on Android WebView.
  const selectedScale = (isSelected ? 1.1 : 1) * sizeScale;
  wrap.style.transform = `scale(${selectedScale})`;
}

function applyExperienceWrapTransform(wrap, sizeScale, isSelected) {
  const selectedScale = (isSelected ? 1.16 : 1) * sizeScale;
  wrap.style.transform = `scale(${selectedScale})`;
}

/**
 * Owns every marker-related side effect on the Guest Map:
 *  - venue marker diff/render (photo pins at every zoom, smoothly resized)
 *  - continuous zoom-driven size update applied via direct DOM mutation
 *  - "selected" visual toggle applied via inner-wrapper CSS ONLY
 *    (never touch the outer element — MapLibre owns its transform)
 *  - the signed-in user's avatar marker (deduped so pan/zoom never
 *    forces a re-mount of its DOM element)
 *  - experience ("Hidden Gem") marker diff/render + smooth resize
 *
 * All markers are diffed against a `stateKey` on the DOM element so
 * pan/zoom doesn't blink pins on every render. Pending-pulse maps from
 * `useTagRefit` are consumed here so pins pulled INTO the viewport by
 * a fitBounds land already pulsing.
 */
export function useGuestMapMarkers({
  mapRef,
  markersRef,
  experienceMarkersRef,
  userMarkerRef,
  isExpandedRef,
  visibleVenues,
  displayedExperiences,
  selectedMapVenueId,
  selectedExperienceId,
  setSelectedMapVenueId,
  setSelectedExperienceId,
  openMapFully,
  userPosition,
  userAvatarUrl,
  locationStatus,
  distanceKmFromUser,
  isFavorite,
  venueEventState,
  markerSize,
  nearbyRadiusKm,
  applyPulseToMarker,
  pendingPulseVenueIdsRef,
  pendingPulseExperienceIdsRef,
  toLngLat,
}) {
  // Current sizes live in refs — they're read when creating a new marker
  // so the pin that just entered the viewport already matches today's
  // zoom. The `zoom` listener below ALSO updates existing markers' DOM
  // directly, so React never re-renders the venue list on zoom.
  const venueSizeRef = useRef(VENUE_SIZE_MAX);
  const experienceSizeRef = useRef(EXPERIENCE_SIZE_MAX);
  // Used to defeat React race conditions where the marker-add effect
  // runs with a slightly stale `visibleVenues` after a style swap — a
  // bumped counter forces the inline resize block below to re-run.
  const forceResizeTickRef = useRef(0);

  // ── Smooth size driver ────────────────────────────────────────
  // Updates all existing markers' DOM sizes on every `zoom` event.
  // Cheap: at most ~30 pins visible at any time, a handful of style
  // writes per frame.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    const applySizes = () => {
      const z = map.getZoom();
      const vSize = venueSizeFor(z);
      const eSize = experienceSizeFor(z);
      venueSizeRef.current = vSize;
      experienceSizeRef.current = eSize;
      const vScale = vSize / VENUE_SIZE_MAX;
      const eScale = eSize / EXPERIENCE_SIZE_MAX;

      markersRef.current.forEach((marker) => {
        const el = marker.getElement();
        if (!el) return;
        const wrap = el.querySelector(':scope > [data-marker-inner]');
        if (!wrap) return;
        wrap.dataset.sizeScale = String(vScale);
        const isSelected = wrap.dataset.selected === '1';
        applyVenueWrapTransform(wrap, vScale, isSelected);
      });

      experienceMarkersRef.current.forEach((marker) => {
        const el = marker.getElement();
        if (!el) return;
        const wrap = el.querySelector(':scope > [data-marker-inner]');
        if (!wrap) return;
        wrap.dataset.sizeScale = String(eScale);
        const isSelected = wrap.dataset.selected === '1';
        applyExperienceWrapTransform(wrap, eScale, isSelected);
      });

      // Co-located venues (same building): keep every pin at least half a
      // pin apart so none is fully hidden. Pixel distances change with
      // zoom, so this runs on every zoom tick alongside the size driver.
      spreadOverlappingMarkers(map, [...markersRef.current.values()], vSize);
    };

    applySizes();
    map.on('zoom', applySizes);
    map.on('zoomend', applySizes);
    // Also re-apply after a style swap so pins are correctly sized the
    // first paint after the real tiles come in.
    map.on('styledata', applySizes);
    return () => {
      map.off('zoom', applySizes);
      map.off('zoomend', applySizes);
      map.off('styledata', applySizes);
    };
    // mapRef is a ref container, stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Venue markers ─────────────────────────────────────────────
  // Diffed against `desiredIds` + a stateKey so we never nuke-and-rebuild
  // on every viewport change. Size never appears in the stateKey — it's
  // applied via the smooth-size driver above.
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    const desiredIds = new Set(visibleVenues.map((v) => v.id));
    markersRef.current.forEach((marker, id) => {
      if (!desiredIds.has(id)) {
        marker.remove();
        markersRef.current.delete(id);
      }
    });

    visibleVenues.forEach((venue, idx) => {
      // distanceKmFromUser + nearbyRadiusKm intentionally unused here —
      // we deliberately keep the photo visible at every distance /
      // zoom to match the "pins should always show the picture" spec.
      void distanceKmFromUser; void nearbyRadiusKm;
      const eventState = venueEventState(venue.id);
      const liked = isFavorite(venue.id);
      const stateKey = `${liked ? 'f' : '_'}|${eventState || '_'}`;

      const existing = markersRef.current.get(venue.id);
      if (existing) {
        const el = existing.getElement();
        if (el && el.dataset.stateKey === stateKey) {
          existing.setLngLat([venue.longitude, venue.latitude]);
          return;
        }
        existing.remove();
        markersRef.current.delete(venue.id);
      }

      const markerElement = createMapMarkerElement({
        imageUrl: getVenueImage(venue, idx),
        // DOM stays at MAX size — zoom-driven resizing is a CSS scale
        // on the inner wrap, so MapLibre's anchor math never budges.
        size: VENUE_SIZE_MAX,
        sizeScale: venueSizeRef.current / VENUE_SIZE_MAX,
        isSelected: selectedMapVenueId === venue.id,
        isFavorite: liked,
        testId: `map-marker-${venue.id}`,
        eventState,
      });
      markerElement.dataset.stateKey = stateKey;
      markerElement.addEventListener('click', (event) => {
        event.stopPropagation();
        // Suppress click events while the map is mid-gesture so a stray
        // fingertip during pinch-zoom doesn't "snap back" to a venue.
        if (map.isMoving() || map.isZooming() || map.isRotating()) return;
        if (!isExpandedRef.current) {
          openMapFully();
          return;
        }
        setSelectedMapVenueId(venue.id);
        setSelectedExperienceId(''); // one preview at a time
        // Only pan/zoom if the user is so far out the venue would be
        // unreadable. Otherwise keep the camera anchored so neighbouring
        // pins stay put — makes tap-to-compare feel natural.
        const currentZoom = map.getZoom();
        if (currentZoom < 12) {
          map.easeTo({
            center: [venue.longitude, venue.latitude],
            zoom: 13.5,
            duration: 600,
            easing: (t) => 1 - ((1 - t) ** 3),
          });
        }
      });

      const marker = new maplibregl.Marker({
        element: markerElement,
        // Center-anchored, same as experience pins. The venue pin's
        // outer DOM is a perfect square and the photo bubble fills it,
        // so geo coord = outer center = photo center at every zoom —
        // no sub-pixel drift during continuous zoom (user-reported
        // "venue flies into the sea when I zoom out" on native Android).
        anchor: 'center',
      })
        .setLngLat([venue.longitude, venue.latitude])
        .addTo(map);
      markersRef.current.set(venue.id, marker);
      // Catch-up pulse for pins pulled INTO the viewport by fitBounds.
      const pulseDeadline = pendingPulseVenueIdsRef.current.get(venue.id);
      if (pulseDeadline && pulseDeadline > Date.now()) {
        applyPulseToMarker(marker);
        pendingPulseVenueIdsRef.current.delete(venue.id);
      }
    });

    // Bump the resize tick so the smooth-size driver re-syncs any
    // freshly-added markers to the current zoom before the next frame.
    forceResizeTickRef.current += 1;
    // Newly-added pins may land on top of an existing one — spread now
    // rather than waiting for the next zoom tick.
    spreadOverlappingMarkers(map, [...markersRef.current.values()], venueSizeRef.current);
    // markerSize / selectedMapVenueId intentionally omitted — applied via
    // cheap DOM mutations in the effects below to avoid re-creating every
    // marker DOM tree on every drag frame or selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleVenues, openMapFully, venueEventState]);

  // Resize any venue pins created THIS tick to match the current zoom.
  // Belt-and-braces: the smooth-size driver already does this on every
  // zoom tick, but newly-added markers (e.g. a venue just pulled into
  // the viewport) need an immediate sync before the next zoom frame so
  // they don't pop at the default scale on their first paint.
  useEffect(() => {
    const vScale = venueSizeRef.current / VENUE_SIZE_MAX;
    markersRef.current.forEach((marker) => {
      const el = marker.getElement();
      if (!el) return;
      const wrap = el.querySelector(':scope > [data-marker-inner]');
      if (!wrap) return;
      wrap.dataset.sizeScale = String(vScale);
      const isSelected = wrap.dataset.selected === '1';
      applyVenueWrapTransform(wrap, vScale, isSelected);
    });
    // Also mirror markerSize prop in case parent wants a one-off scale.
    void markerSize;
  }, [markerSize, markersRef, visibleVenues]);

  // Cheaply toggle "selected" visuals on existing markers.
  // CRITICAL: NEVER touch `transform`/`filter` on the outer marker element.
  // MapLibre uses the outer element's `transform` to position the marker
  // geographically — overwriting it snaps every pin to its parent's origin,
  // i.e. "all pins gather in a corner". All visual state lives on the
  // inner `[data-marker-inner]` wrapper instead. We ALSO never resize
  // the outer box here — see the anchor-stability note on the smooth
  // size driver at the top of this file.
  useEffect(() => {
    markersRef.current.forEach((marker, id) => {
      const el = marker.getElement();
      if (!el) return;
      const isSelected = selectedMapVenueId === id;
      // Raise the tapped pin above a co-located neighbour so its ring is
      // never hidden. z-index only — never touch position/transform.
      el.style.zIndex = isSelected ? '3' : '';
      const wrap = el.querySelector(':scope > [data-marker-inner]');
      if (!wrap) return;
      wrap.dataset.selected = isSelected ? '1' : '0';
      const sizeScale = Number(wrap.dataset.sizeScale) || 1;
      applyVenueWrapTransform(wrap, sizeScale, isSelected);
      // Round-7: visual decoration restored. Safe because the inner
      // wrap stays on its parent's compositor layer (no translateZ /
      // willChange) so box-shadow/border never subpixel-drifts.
      wrap.style.border = isSelected ? '3px solid #EAF0FF' : '3px solid rgba(255,255,255,0.92)';
      wrap.style.boxShadow = isSelected
        ? '0 0 0 4px rgba(113, 90, 247, 0.28), 0 6px 14px rgba(8, 12, 24, 0.24)'
        : '0 6px 14px rgba(8, 12, 24, 0.24)';
    });
  }, [selectedMapVenueId, markersRef]);

  // ── Signed-in user's avatar marker ────────────────────────────
  // Deduped: we only recreate the DOM element when the VISUAL state
  // changes (locationStatus / userAvatarUrl). On every userPosition
  // tick we just call setLngLat — this prevents the user-reported
  // "blue dot jitter while zooming" caused by the previous
  // unconditional remove+recreate on every effect re-run.
  const userMarkerVisualKeyRef = useRef('');
  useEffect(() => {
    if (!mapRef.current) return;
    if (!userPosition) {
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
      userMarkerVisualKeyRef.current = '';
      return;
    }
    const lngLat = toLngLat(userPosition);
    const visualKey = `${locationStatus === 'live' ? 'live' : 'fallback'}|${userAvatarUrl || ''}`;
    if (userMarkerRef.current && userMarkerVisualKeyRef.current === visualKey) {
      userMarkerRef.current.setLngLat(lngLat);
      return;
    }
    userMarkerRef.current?.remove();
    const marker = new maplibregl.Marker({
      element: createUserMarkerElement(userAvatarUrl, { isLive: locationStatus === 'live' }),
      anchor: 'center',
    })
      .setLngLat(lngLat)
      .addTo(mapRef.current);
    userMarkerRef.current = marker;
    userMarkerVisualKeyRef.current = visualKey;
  }, [toLngLat, userPosition, userAvatarUrl, locationStatus, mapRef, userMarkerRef]);

  // ── Experience ("Hidden Gems") markers ───────────────────────
  // Mirror of venue diff pattern. Size is driven by the smooth-size
  // effect at the top of this hook, so size doesn't enter the stateKey.
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const desiredIds = new Set(displayedExperiences.map((e) => e.id));
    experienceMarkersRef.current.forEach((marker, id) => {
      if (!desiredIds.has(id)) {
        marker.remove();
        experienceMarkersRef.current.delete(id);
      }
    });
    displayedExperiences.forEach((exp) => {
      const stateKey = `${exp.category}|${selectedExperienceId === exp.id ? 's' : '_'}`;
      const existing = experienceMarkersRef.current.get(exp.id);
      if (existing) {
        const el = existing.getElement();
        if (el && el.dataset.stateKey === stateKey) {
          existing.setLngLat([exp.longitude, exp.latitude]);
          return;
        }
        existing.remove();
        experienceMarkersRef.current.delete(exp.id);
      }
      const el = createExperienceMarkerElement({
        category: exp.category,
        isSelected: selectedExperienceId === exp.id,
        // DOM stays at MAX size — see note on createMapMarkerElement.
        sizeScale: experienceSizeRef.current / EXPERIENCE_SIZE_MAX,
        testId: `guest-map-experience-marker-${exp.id}`,
      });
      el.dataset.stateKey = stateKey;
      el.addEventListener('click', (event) => {
        event.stopPropagation();
        if (map.isMoving() || map.isZooming() || map.isRotating()) return;
        if (!isExpandedRef.current) {
          openMapFully();
          return;
        }
        setSelectedMapVenueId(null);
        setSelectedExperienceId(exp.id);
      });
      const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([exp.longitude, exp.latitude])
        .addTo(map);
      experienceMarkersRef.current.set(exp.id, marker);
      const pulseDeadline = pendingPulseExperienceIdsRef.current.get(exp.id);
      if (pulseDeadline && pulseDeadline > Date.now()) {
        applyPulseToMarker(marker);
        pendingPulseExperienceIdsRef.current.delete(exp.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedExperiences, selectedExperienceId, openMapFully]);
}
